import type { ReactNode } from 'react'

export interface FormActionsProps {
  /** The action buttons (e.g. Cancel + Submit), in desktop left→right order. */
  children: ReactNode
  /** Extra classes for the inline (desktop) container. */
  className?: string
  /**
   * EP-80 #8: on mobile, pin the form actions to the bottom of the viewport (a
   * fixed bar + a scroll spacer) so the submit button stays reachable without
   * scrolling to the end of a long form. On lg+ it renders inline exactly like
   * the previous `flex justify-end gap-3` row. Form pages scroll inside
   * `<main overflow-auto>`, so true CSS `sticky` can't travel — hence fixed +
   * spacer (same approach as `Pagination`). `type="submit"` children still
   * submit the parent <form> (fixed positioning doesn't detach them from it).
   * Set false for short/inline/modal forms that don't need it.
   */
  stickyOnMobile?: boolean
}

export function FormActions({ children, className = '', stickyOnMobile = true }: FormActionsProps) {
  if (!stickyOnMobile) {
    return <div className={`flex items-center justify-end gap-3 ${className}`}>{children}</div>
  }

  return (
    <>
      {/* Mobile-only spacer so the last field clears the fixed bar. No-op on lg+. */}
      <div aria-hidden className="lg:hidden h-[calc(4rem+env(safe-area-inset-bottom))]" />
      {/* Mobile: fixed bottom action bar (z-30 sits below the sidebar backdrop z-40).
          lg+: reverts to the inline row, unchanged. */}
      <div
        data-testid="form-actions-bar"
        className={`fixed inset-x-0 bottom-0 z-30 flex items-center justify-end gap-3 border-t border-base-200 bg-base-100 px-4 py-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] shadow-[0_-2px_8px_rgba(0,0,0,0.04)] lg:static lg:inset-x-auto lg:z-auto lg:border-0 lg:bg-transparent lg:px-0 lg:py-0 lg:shadow-none ${className}`}
      >
        {children}
      </div>
    </>
  )
}
